import type { Leaderboard, SalaryModel } from '@brush/api-client';
import { BuilderSection, primaryButtonClass } from '@brush/ui';
import type { ReactNode } from 'react';
import {
  describeLocksAt,
  rosterFormatLabel,
  withUnit,
  type ContestDraft,
} from '../../fantasy/contest';

/**
 * Section 6 — what this contest says, in one place, and the button that makes it permanent.
 *
 * <b>A restatement, not a summary.</b> The five sections above are worked through in order and each
 * of them is a decision made in isolation; this is the only place they are read together, which is
 * where "eight players under a budget of 200, out of a pool of twelve" becomes visible as absurd.
 * So every line here is a fact already set above rather than a new control, and the whole point is
 * that it is read once more before the freeze.
 *
 * <b>The blockers are reasons, never a disabled button on its own.</b> Publish is what an operator
 * opened this screen to reach, and a greyed one with no explanation reads as a broken app. The
 * server refuses all of these properly too — this saves the round trip and names the section.
 */
export function ContestReviewSection({
  step,
  draft,
  leaderboard,
  eventsInScope,
  poolSize,
  needsSalaries,
  model,
  modelVersion,
  locksAt,
  blockers,
  onPublish,
  publishing,
}: {
  step: number;
  draft: ContestDraft;
  leaderboard: Leaderboard | null;
  /** Null until the resolution lands — "not counted yet" must not read as zero. */
  eventsInScope: number | null;
  poolSize: number;
  needsSalaries: boolean;
  /** The model publish will freeze, resolved for display. Null is nothing chosen yet. */
  model: SalaryModel | null;
  /** Its version number, for display beside the name. */
  modelVersion: number | null;
  /** `yyyy-mm-ddThh:mm` from section 5, or empty for the rule. */
  locksAt: string;
  blockers: readonly string[];
  onPublish: () => void;
  publishing: boolean;
}) {
  const unit = draft.budgetUnitLabel.trim() || 'units';

  return (
    <BuilderSection step={step} title="Review and publish">
      <div className="space-y-4">
        <dl className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
          <Line label="Name">{draft.name.trim() || <Missing>Not set</Missing>}</Line>
          <Line label="Scores from">
            {leaderboard ? (
              <>
                {leaderboard.name}
                <span className="text-ink-800/50">
                  {' · '}
                  {eventsInScope === null
                    ? 'counting events…'
                    : `${eventsInScope} ${eventsInScope === 1 ? 'event' : 'events'}`}
                </span>
              </>
            ) : (
              <Missing>Not set</Missing>
            )}
          </Line>
          <Line label="Format">{rosterFormatLabel(draft.rosterFormat)}</Line>
          <Line label="Roster">
            {draft.rosterSize ?? <Missing>Not set</Missing>} players
            {draft.captainEnabled ? `, one of them captain at ×${draft.captainMultiplier ?? '?'}` : ''}
          </Line>
          <Line label="Budget">
            {draft.budget === null ? <Missing>Not set</Missing> : withUnit(draft.budget, unit)}
          </Line>
          <Line label="Entry">
            {/* §12: the fee is modelled and constrained to zero, so this is a fact rather than a
                field. Stated rather than omitted — "what does it cost to enter" is the first thing
                anybody asks about a contest. */}
            Free
            {draft.maxEntriesPerPlayer && draft.maxEntriesPerPlayer > 1
              ? `, up to ${draft.maxEntriesPerPlayer} teams each`
              : ', one team each'}
          </Line>
          {needsSalaries ? (
            <>
              <Line label="Priced by">
                {model ? (
                  <>
                    {model.name}
                    {modelVersion !== null ? (
                      <span className="text-ink-800/50"> · v{modelVersion}</span>
                    ) : null}
                  </>
                ) : (
                  <Missing>Not set</Missing>
                )}
              </Line>
              <Line label="Draftable">
                {poolSize} {poolSize === 1 ? 'player' : 'players'}
              </Line>
            </>
          ) : null}
          <Line label="Entry closes">
            {describeLocksAt(locksAt, 'When the first event starts')}
          </Line>
        </dl>

        {/* The one claim that matters most, made before the button rather than only in the dialog
            behind it. Signal orange because it is the thing that stops being possible. */}
        <p className="border border-line border-l-4 border-l-signal bg-surface p-3 text-[12px] leading-relaxed text-ink-900">
          <strong className="text-ink-950">Publishing is permanent.</strong> The pool and every
          price in it freeze, and so do the budget, the roster size, the captain rule and the
          leaderboard this scores from. Nothing above can be changed afterwards — by anybody. What
          publishing does <em>not</em> do is start the scoring: that happens when entry locks.
        </p>

        {blockers.length > 0 ? (
          <ul className="space-y-1.5" role="alert">
            {blockers.map((blocker) => (
              <li key={blocker} className="flex gap-1.5 text-[11.5px] leading-relaxed text-bad">
                <span aria-hidden>✕</span>
                <span>{blocker}</span>
              </li>
            ))}
          </ul>
        ) : null}

        <button
          type="button"
          onClick={onPublish}
          disabled={blockers.length > 0 || publishing}
          className={primaryButtonClass}
        >
          {publishing ? 'Publishing…' : 'Publish and open entry'}
        </button>
      </div>
    </BuilderSection>
  );
}

function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-[0.08em] text-ink-800/45">{label}</dt>
      <dd className="mt-0.5 text-[13px] text-ink-950">{children}</dd>
    </div>
  );
}

/** An unset field reads as unset rather than as blank, which is indistinguishable from a bug. */
function Missing({ children }: { children: ReactNode }) {
  return <span className="text-signal">{children}</span>;
}
